<script>
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store";
import { Role, RoleMap } from "@/enums/navigation.ts";

export default {
  name: "DispatcherSelect",
  props: {
    id: String,
    modelValue: [Number, String, Array],
    label: String,
    required: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    displayedSelect: {
      type: Number,
      default: 1,
    },
    showRoles: {
      type: Array,
      default: () => [],
    },
    clearOnSelect: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:modelValue", "search"],
  data() {
    return {
      hasValueChanged: false,
    };
  },
  computed: {
    RoleMap() {
      return RoleMap;
    },
    ...mapState(useAppStore, ["dispatchers"]),
    isLoading() {
      return !this.dispatchers || this.dispatchers.length === 0;
    },
    value: {
      get() {
        return this.isLoading ? "Loading..." : this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    dispatchersToShow() {
      const sorted = [...this.dispatchers].sort((a, b) => {
        const hasABooking = a.roles.includes(Role.booking) ? 0 : 1;
        const hasBBooking = b.roles.includes(Role.booking) ? 0 : 1;

        return hasABooking - hasBBooking;
      });

      if (!this.showRoles.length) {
        return sorted;
      }

      return sorted.filter((dispatcher) => {
        return this.showRoles.some((role) => dispatcher.roles.includes(role));
      });
    },
  },
  watch: {
    value: {
      handler(newValue, oldValue) {
        this.hasValueChanged = JSON.stringify(newValue) !== JSON.stringify(oldValue);
      },
    },
  },
  methods: {
    onBlur() {
      if (this.hasValueChanged) {
        this.$emit("search", this.value);

        this.hasValueChanged = false;
      }
    },
  },
};
</script>

<template>
  <v-autocomplete
    :id="id"
    v-model="value"
    :class="{ required }"
    :clear-on-select="clearOnSelect"
    :clearable="true"
    :items="dispatchersToShow"
    :label="label || $t('dispatchers.dispatcher')"
    :loading="isLoading"
    :multiple="multiple"
    class="dispatcher-select"
    color="primary"
    density="compact"
    hide-details="auto"
    item-title="name"
    item-value="id"
    variant="outlined"
    @blur="onBlur"
  >
    <template #item="{ item, props }">
      <v-list-item :title="item.raw.name" v-bind="props">
        <div class="d-flex align-center">
          <div v-for="(role, index) of item.raw.roles" :key="role" class="text-grey-darken-1 mr-2px">
            <span>{{ RoleMap[role] }}</span>
            <span v-if="index !== item.raw.roles.length - 1">,</span>
          </div>
        </div>
      </v-list-item>
    </template>

    <template #selection="{ item, index }">
      <span
        v-if="displayedSelect > index"
        class="d-flex align-center white-space-nowrap overflow-hidden text-overflow-ellipsis"
      >
        <span class="text-grey-darken-4 mr-2px">{{ item.raw.name }}</span>

        <span class="text-grey-darken-2">
          <span v-if="index && index !== value.length - 1">,</span>
        </span>
      </span>

      <span v-if="index === displayedSelect">...</span>
    </template>
  </v-autocomplete>
</template>

<style lang="scss" scoped>
.dispatcher-select {
  &:deep(.v-field__input) {
    flex-wrap: nowrap;
  }
}
</style>
